/**
 * useDivisionsListener Hook
 *
 * Sets up a real-time Firestore listener on the divisions collection.
 * When divisions change (admin creates/updates/deletes divisions), the store updates immediately.
 *
 * Uses the generic useFirestoreCollection hook for consistency.
 * Pattern: useRolesListener.ts
 *
 * Usage:
 *   // In AuthenticatedLayout after initial authentication
 *   useDivisionsListener(isConnected);
 */

import { useCallback } from 'react';
import { orderBy } from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { useFirebaseStore } from '../store/firebaseStore';
import { useDivisionsStore } from '../store/divisionsStore';
import { useFirestoreCollection } from './firestore';
import { getDivisionsCollectionPath } from '../config/collections';
import { logger } from '../utils/logger';
import type { DivisionDefinition } from '../types/divisions.types';

/**
 * Parse Firestore document into DivisionDefinition type
 */
function parseDivisionDocument(id: string, data: DocumentData): DivisionDefinition | null {
  // Validate required fields
  if (!data.name) {
    logger.warn(`[useDivisionsListener] Invalid division document ${id}: missing name`);
    return null;
  }

  return {
    id,
    name: data.name || '',
    locationIds: data.locationIds || [],
    sortOrder: data.sortOrder ?? 0,
    isActive: data.isActive ?? true,
    createdBy: data.createdBy || '',
    createdAt: data.createdAt,
    lastEditedBy: data.lastEditedBy || '',
    lastEditedAt: data.lastEditedAt,
  };
}

/**
 * Setup real-time listener for division definitions
 *
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent false "not found" warnings before Firebase auth completes.
 */
export const useDivisionsListener = (enabled: boolean = true): void => {
  const db = useFirebaseStore((state) => state.db);
  const setDivisions = useDivisionsStore((state) => state.setDivisions);
  const setLoading = useDivisionsStore((state) => state.setLoading);
  const setError = useDivisionsStore((state) => state.setError);
  const setInitialized = useDivisionsStore((state) => state.setInitialized);

  // Collection path
  const collectionPath = getDivisionsCollectionPath();

  // Stable callback for handling loaded data
  const handleDataLoaded = useCallback(
    (divisions: DivisionDefinition[]) => {
      setDivisions(divisions);
      setInitialized(true);
      logger.log(`[useDivisionsListener] Loaded ${divisions.length} divisions`);
    },
    [setDivisions, setInitialized]
  );

  // Stable callback for handling loading state
  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setLoading(loading);
    },
    [setLoading]
  );

  // Stable callback for handling errors
  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      logger.error(`[useDivisionsListener] Error listening to divisions:`, errorMessage);
    },
    [setError]
  );

  // Use generic collection listener hook
  useFirestoreCollection<DivisionDefinition>({
    db,
    collectionPath,
    queryConstraints: [orderBy('sortOrder', 'asc')],
    parseDocument: parseDivisionDocument,
    onDataLoaded: handleDataLoaded,
    onLoadingChange: handleLoadingChange,
    onError: handleError,
    enabled: enabled && !!db,
  });
};
